import { Badge } from '@shared/components/ui/badge';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePackingStationDetail } from '../../hooks/usePackingStationDetail';
import type { PackingStation, PackingStationDetail, PackingStationRelationsTabKey } from '../../models/PackingStation';
import { PackingStationRelationsPanel } from './PackingStationRelationsPanel';
import { resolvePackingStationLocationCode } from '../../utils/resolvePackingStationLocationCode';

export interface DetailModalRef {
  open: (station: PackingStation, initialTab?: PackingStationRelationsTabKey) => void;
}

export const DetailModal = forwardRef<DetailModalRef, { onRefetch?: () => void }>((props, ref) => {
  const { t } = useTranslation('packingStationsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [stationId, setStationId] = useState<number | null>(null);
  const [initialDetail, setInitialDetail] = useState<PackingStationDetail | null>(null);
  const [initialTab, setInitialTab] = useState<PackingStationRelationsTabKey>('printers');

  const buildInitialDetailFromStation = (station: PackingStation): PackingStationDetail => {
    const rawStation = station as Record<string, unknown>;
    const rawPrinters = Array.isArray(rawStation.printers) ? rawStation.printers : [];
    const rawClients = Array.isArray(rawStation.clients) ? rawStation.clients : [];
    const rawLocations = Array.isArray(rawStation.locations) ? rawStation.locations : [];

    const printers = rawPrinters.map((item) => {
      const printer = item as Record<string, unknown>;
      const nested = printer.printer as Record<string, unknown> | undefined;
      const name = typeof printer.name === 'string' ? printer.name.trim() : '';
      const code = typeof printer.printer_code === 'string' ? printer.printer_code.trim() : '';
      return {
        ...printer,
        name: name || (typeof nested?.name === 'string' ? nested.name.trim() : ''),
        printer_code: code || (typeof nested?.code === 'string' ? nested.code.trim() : ''),
      };
    });

    const clients = rawClients.map((item) => {
      const client = item as Record<string, unknown>;
      const nestedTeam = client.team as Record<string, unknown> | undefined;
      const name = typeof client.name === 'string' ? client.name.trim() : '';
      return {
        ...client,
        name: name || (typeof nestedTeam?.name === 'string' ? nestedTeam.name.trim() : ''),
      };
    });

    const locations = rawLocations.map((item) => {
      const location = item as Record<string, unknown>;
      return {
        ...location,
        code: resolvePackingStationLocationCode(location),
      };
    });

    return {
      ...(station as PackingStationDetail),
      printers: printers as PackingStationDetail['printers'],
      clients: clients as PackingStationDetail['clients'],
      locations: locations as PackingStationDetail['locations'],
    };
  };

  useImperativeHandle(ref, () => ({
    open: (station: PackingStation, tab?: PackingStationRelationsTabKey) => {
      setStationId(station.id);
      setInitialDetail(buildInitialDetailFromStation(station));
      setInitialTab(tab ?? 'printers');
      modalRef.current?.openModal();
    },
  }));

  const { detail, isLoading, refetch } = usePackingStationDetail(stationId, initialDetail);

  const resolvedDetail = useMemo(() => detail ?? initialDetail, [detail, initialDetail]);

  const handleInvalidate = () => {
    setInitialDetail(null);
    void refetch();
    props.onRefetch?.();
  };

  const renderModalTitle = () => {
    if (!resolvedDetail) return t('detail.title');
    return (
      <div className="flex items-center gap-3">
        <span className="font-mono font-semibold">{resolvedDetail.code}</span>
        {resolvedDetail.active ? (
          <Badge variant="default" className="text-xs bg-bg-success text-success border-0">
            {t('table.status.active')}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs text-title">
            {t('table.status.inactive')}
          </Badge>
        )}
      </div>
    );
  };

  return (
    <ModalCenter ref={modalRef} title={renderModalTitle()} type="default" width="xl">
      {stationId !== null && (
        <PackingStationRelationsPanel
          stationId={stationId}
          detail={resolvedDetail ?? null}
          isLoadingDetail={isLoading}
          onInvalidate={handleInvalidate}
          initialTab={initialTab}
        />
      )}
    </ModalCenter>
  );
});

DetailModal.displayName = 'DetailModal';
